import {useDisclosure} from '@mantine/hooks';
import {
  MdMoreHoriz,
  MdOutlineThumbUp,
  MdReply,
  MdThumbUp,
} from 'react-icons/md';
import {COLOR_BLACK_2} from '../../../../../config/constants/colorCodes';
import type {CommunityComment} from '../../../../../core/entities/community/schema';
import {useHandleLikedCommunityComment} from '../../../../../core/usecases/community/useHandleLikegCommunityComment';
import {NoLoginAlertModal} from '../../../../../shared/components/NoLoginAlertModal';
import {formatDateFullWithTime} from '../../../../../shared/services/formatDate';
import {convertCommentBody} from '../../../usecases/convertCommentBody';
import {PostDoubtCommentModal} from '../PostDoubtCommentModal';
import {CommentItemMoreMenu} from './CommentItemMoreMenu';
import {ReportCommentModal} from './ReportCommentModal';

type Props = {
  topicId: number;
  comment: CommunityComment;
  openReplyModalFunc: (seqNumber: number) => void;
  onClickReplyButton: (seqNumber: number) => void;
  isSignedIn: boolean;
};

export const CommentListItem: React.FC<Props> = ({
  topicId,
  comment,
  openReplyModalFunc,
  onClickReplyButton,
  isSignedIn,
}) => {
  const [isMoreMenuOpened, {open: openMoreMenu, close: closeMoreMenu}] =
    useDisclosure(false);
  const [
    isReportModalOpened,
    {open: openReportModal, close: closeReportModal},
  ] = useDisclosure(false);
  const [isDoubtModalOpened, {open: openDoubtModal, close: closeDoubtModal}] =
    useDisclosure(false);
  const [
    isNoLoginModalOpened,
    {open: openNoLoginModal, close: closeNoLoginModal},
  ] = useDisclosure(false);
  const {likedCount, isLiked, handleLikedCommunityComment} =
    useHandleLikedCommunityComment({
      topicId,
      seqNumber: comment.seqNumber,
      likedCountInitial: comment.likeCount,
      isLikedInitial: comment.liked,
    });

  return (
    <li className="border-b-[1px] border-line border-collapse pb-16 mb-16 [&:nth-last-child(1)]:border-b-[0px] [&:nth-last-child(1)]:pb-0 [&:nth-last-child(1)]:mb-0">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <p className="text-base text-black-2 font-bold leading mr-8">
            {comment.seqNumber}
          </p>
          <p
            className={`${comment.byAdmin ? 'text-notification-1' : 'text-black-2'} text-base font-bold leading`}
          >
            {comment.nickname}
          </p>
        </div>
        <p className="text-sm text-black-2 font-bold leading">
          {formatDateFullWithTime(comment.createdAt)}
        </p>
      </div>
      <p className="text-base text-black-2 leading mt-8">
        {convertCommentBody({text: comment.body, openReplyModalFunc})}
      </p>
      <div className="flex justify-end items-center mt-8">
        <div className="relative mr-12">
          <button
            type="button"
            className="hover:opacity-80"
            onClick={() => {
              if (!isSignedIn) {
                openNoLoginModal();
                return;
              }
              isMoreMenuOpened ? closeMoreMenu() : openMoreMenu();
            }}
          >
            <MdMoreHoriz size={24} color={COLOR_BLACK_2} />
          </button>
          <CommentItemMoreMenu
            opened={isMoreMenuOpened}
            close={closeMoreMenu}
            openReportModal={openReportModal}
            openDoubtModal={openDoubtModal}
          />
        </div>
        <div className="mr-12">
          <button
            type="button"
            className="flex justify-center items-center hover:opacity-80"
            onClick={async () => {
              if (!isSignedIn) {
                openNoLoginModal();
                return;
              }
              await handleLikedCommunityComment();
            }}
          >
            <div className="mr-4">
              {isLiked ? (
                <MdThumbUp size={20} fill={COLOR_BLACK_2} />
              ) : (
                <MdOutlineThumbUp size={20} fill={COLOR_BLACK_2} />
              )}{' '}
            </div>
            <p className="text-base text-black-2 font-bold leading">
              {likedCount}
            </p>
          </button>
        </div>
        <button
          type="button"
          className="flex justify-center items-center hover:opacity-80"
          onClick={() => {
            if (!isSignedIn) {
              openNoLoginModal();
              return;
            }
            onClickReplyButton(comment.seqNumber);
          }}
        >
          <div className="mr-4">
            <MdReply size={20} fill={COLOR_BLACK_2} />
          </div>
          <p className="text-base text-black-2 font-bold leading">返信</p>
        </button>
      </div>
      <PostDoubtCommentModal
        opened={isDoubtModalOpened}
        open={openDoubtModal}
        close={closeDoubtModal}
        topicId={topicId}
        comment={comment}
      />
      <ReportCommentModal
        opened={isReportModalOpened}
        open={openReportModal}
        close={closeReportModal}
        topicId={topicId}
        comment={comment}
      />
      <NoLoginAlertModal
        opened={isNoLoginModalOpened}
        onClose={closeNoLoginModal}
      />
    </li>
  );
};
